import { useEffect, useState } from 'react';
import { NextLink, Translate, useTranslate } from '~/i18n';
import { Router, useRouter } from 'next/router';
import NProgress from 'nprogress';
import cn from 'classnames';
import { headerLinkClickDataLayer } from '~/analytics/gtm/navigation';
import { useAuth } from '~/auth';
import { LanguageMenu } from '~/components/layout/language-menu';
import { MobileMenu } from '~/components/layout/header/mobile-header';
import UserDropdown from '~/components/layout/header/user-dropdown';
import { header_links } from '~/components/layout/header/header_links';
import Container from '~/components/common/container';
import LoginModal from '~/components/login-steps/modal';
import { SearchIcon, UserOutlineIcon } from '~/svgs/navbar';
import Image from 'next/image';
import Button from '~/components/common/button';

interface WebsiteHeaderProps {
   transparentNavbar?: boolean;
   langHref?: string;
   hideSearchbar?: boolean;
}

const WebsiteHeader = ({ transparentNavbar, langHref }: WebsiteHeaderProps) => {
   const { locale, lang } = useTranslate();
   const { authenticated } = useAuth();
   const [scrolled, setScrolled] = useState(false);
   const [open, setOpen] = useState(false);
   const router = useRouter();
   useEffect(() => {
      const handleScroll = () => setScrolled(window.scrollY >= 20);
      if (transparentNavbar) {
         window.addEventListener('scroll', handleScroll);
         handleScroll();
      }
      return () => window.removeEventListener('scroll', handleScroll);
   }, [transparentNavbar]);

   useEffect(() => {
      Router.events.on('routeChangeStart', NProgress.start);
      Router.events.on('routeChangeComplete', NProgress.done);
      Router.events.on('routeChangeError', NProgress.done);

      return () => {
         Router.events.off('routeChangeStart', NProgress.start);
         Router.events.off('routeChangeComplete', NProgress.done);
         Router.events.off('routeChangeError', NProgress.done);
      };
   }, []);

   const headerClassNames = cn(
      'h-[80px] py-4 transition-all duration-300 bg-black/20 font-primary',
      {
         '!bg-white !text-black border-b border-gray-border':
            (transparentNavbar && scrolled) || !transparentNavbar,
      },
      transparentNavbar &&
         !scrolled &&
         '!bg-transparent !text-white border-b border-transparent [&>*]:fill-white'
   );

   const logoSrc = `/madinah/logo${!transparentNavbar || scrolled ? '' : '-white'}.svg`;

   return (
      <div className={cn('inset-x-0 top-0 z-50', transparentNavbar ? 'fixed' : 'sticky')}>
         <LoginModal
            open={open}
            onClose={() => setOpen(false)}
            openOtp={setOpen}
            redirectBanner={() => false}
         />
         <header className={headerClassNames}>
            <Container className="xl:hidden">
               <MobileMenu langHref={langHref} transparentNavbar={transparentNavbar} />
            </Container>

            <Container className="hidden xl:flex w-full gap-x-4">
               <div className="flex items-center justify-between w-full gap-x-4">
                  <nav className="w-full flex items-center space-x-4 text-white uppercase  rtl:space-x-reverse tablet:text-base">
                     {header_links.map((link, idx) => {
                        const Tag = link.external ? 'a' : NextLink;

                        return (
                           <Tag
                              key={idx}
                              href={`${link.href.replace(':lang', locale)}${link?.section ? `?category=${link?.section}` : ''}`}
                              onClick={() =>
                                 headerLinkClickDataLayer({
                                    label: link.title[locale],
                                    link: `${link.href.replace(':lang', locale)}${link?.section ? `?category=${link?.section}` : ''}`,
                                 })
                              }
                              className={cn(
                                 'hover:opacity-70 focus:underline focus:outline-none font-semibold text-sm ',
                                 {
                                    'text-black': !transparentNavbar || scrolled,
                                 }
                              )}
                              {...(link.external && {
                                 rel: 'noopener noreferrer',
                                 target: '_blank',
                              })}>
                              {link.title[locale]}
                           </Tag>
                        );
                     })}
                  </nav>

                  <div className=" flex w-fit shrink-0 items-center mx-auto">
                     <NextLink href="/" title="Al Madinah" className="w-full">
                        <Image src={logoSrc} width="94" height="45" alt="Al Madinah" />
                     </NextLink>
                  </div>

                  <div className="flex items-center justify-end space-x-4 rtl:space-x-reverse !text-base w-full">
                     <Button
                        type={transparentNavbar && !scrolled ? 'outline-white' : 'outline-black'}
                        onClick={() => router.push(`/${lang}/calendar`)}
                        className="py-0.5 !px-4 !text-sm leading-none">
                        <Translate id="common:events calendar" />
                     </Button>

                     <NextLink
                        href="/search"
                        aria-label="Search"
                        className={cn(
                           'hover:opacity-70 focus:underline focus:outline-none text-sm  flex items-center font-semibold gap-x-2',
                           {
                              '!text-black': !transparentNavbar || scrolled,
                           }
                        )}>
                        <SearchIcon
                           className={cn({
                              '[&>*]:fill-black': !transparentNavbar || scrolled,
                              '[&>*]:fill-white': transparentNavbar && !scrolled,
                           })}
                        />
                        <Translate id="common:search" />
                     </NextLink>

                     {!authenticated ? (
                        <button
                           onClick={() => setOpen(true)}
                           className={cn(
                              'hover:opacity-70 focus:underline focus:outline-none  flex items-center font-semibold gap-x-2',
                              {
                                 'text-black': !transparentNavbar || scrolled,
                              }
                           )}>
                           <div className="flex items-center gap-x-2 text-sm">
                              <UserOutlineIcon
                                 className={cn(
                                    transparentNavbar && !scrolled && '[&>path]:fill-white'
                                 )}
                              />
                              <Translate id="auth:signin or signup" />
                           </div>
                        </button>
                     ) : (
                        <UserDropdown />
                     )}

                     <div
                        className={cn('rounded-full', {
                           '!text-black': !transparentNavbar || scrolled,
                        })}>
                        <LanguageMenu
                           langHref={langHref}
                           black_icon={!transparentNavbar || scrolled}
                           className="!text-sm"
                        />
                     </div>
                  </div>
               </div>
            </Container>
         </header>
      </div>
   );
};

export default WebsiteHeader;
